<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

const props = defineProps<{
  contactUrl?: string | null
  cleanupLabel?: string | null
  cleanupBusy?: boolean
  cleanupError?: string | null
}>()

const emit = defineEmits<{
  cleanup: []
}>()

const safeContactURL = computed(() => {
  const raw = props.contactUrl?.trim()
  if (!raw) return ''
  try {
    const parsed = new URL(raw, window.location.origin)
    return parsed.protocol === 'https:'
      ? parsed.href
      : ''
  } catch {
    return ''
  }
})
</script>

<template>
  <aside class="player-verification-notice" role="status" aria-labelledby="player-verification-title">
    <span class="player-verification-icon" aria-hidden="true">!</span>
    <span class="player-verification-copy">
      <strong id="player-verification-title">Нужно подтверждение</strong>
      <span class="player-verification-description">
        Аккаунт Steam авторизован, но ещё не подтверждён.
        Подайте заявку на доступ — её рассмотрит администратор.
      </span>
      <span v-if="cleanupError" class="player-verification-error" role="alert">
        {{ cleanupError }}
      </span>
    </span>
    <span class="player-verification-actions">
      <button
        v-if="cleanupLabel"
        class="player-verification-link"
        type="button"
        :disabled="cleanupBusy"
        @click="emit('cleanup')"
      >
        {{ cleanupBusy ? 'Подождите…' : cleanupLabel }}
      </button>
      <RouterLink class="player-verification-link player-verification-apply" to="/apply">
        Подать заявку
      </RouterLink>
      <a
        v-if="safeContactURL"
        class="player-verification-link"
        :href="safeContactURL"
        target="_blank"
        rel="noopener noreferrer"
      >
        Открыть Discord
      </a>
    </span>
  </aside>
</template>

<style scoped>
.player-verification-notice {
  grid-area: chat;
  min-width: 0;
  min-height: var(--player-dock-control-height);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 14px;
  border: 1px solid rgba(214, 174, 80, 0.48);
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(52, 40, 20, 0.96), rgba(30, 26, 20, 0.94));
  color: #f3e3b4;
}

.player-verification-icon {
  display: grid;
  place-items: center;
  width: 23px;
  height: 23px;
  flex: 0 0 23px;
  border: 1px solid rgba(241, 199, 95, 0.62);
  border-radius: 50%;
  color: #f1c75f;
  font-size: 14px;
  font-weight: 800;
}

.player-verification-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 1px;
  font-size: 12px;
  line-height: 1.3;
}

.player-verification-copy strong {
  color: #fff2c9;
  font-size: 13px;
  line-height: 1.15;
  white-space: nowrap;
}

.player-verification-description {
  color: rgba(237, 226, 198, 0.78);
}

.player-verification-apply {
  border-color: #f1c75f;
  background: rgba(241, 199, 95, 0.14);
}

.player-verification-error {
  color: #ffb3aa;
}

.player-verification-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
}

.player-verification-link {
  flex: 0 0 auto;
  padding: 6px 10px;
  border: 1px solid rgba(241, 199, 95, 0.64);
  border-radius: 3px;
  background: transparent;
  color: #f1c75f;
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  text-decoration: none;
}

.player-verification-link:hover,
.player-verification-link:focus-visible {
  border-color: #f1c75f;
  background: rgba(241, 199, 95, 0.1);
  outline: none;
}

.player-verification-link:disabled {
  cursor: default;
  opacity: 0.55;
}

@media (max-width: 760px) {
  .player-verification-notice {
    grid-column: 1 / -1;
    gap: 9px;
    padding: 7px 10px;
  }

  .player-verification-actions {
    flex-direction: column;
    align-items: stretch;
  }
}

@media (max-width: 900px) {
  .player-verification-notice {
    display: grid;
    grid-template-columns: 23px minmax(0, 1fr);
  }

  .player-verification-actions {
    grid-column: 2;
    flex-flow: row wrap;
  }
}
</style>
